import produce from "immer";
import {
  AUTO_FEED_ACTUALS_STARTED,
  AUTO_FEED_ACTUALS_PROJECT_STARTED,
  AUTO_FEED_ACTUALS_PROJECT_COMPLETED,
  AUTO_FEED_ACTUALS_PROJECT_FAILED,
  AUTO_FEED_ACTUALS_ALL_COMPLETED,
  AUTO_FEED_ACTUALS_ALL_FAILED,
  AUTO_FEED_ACTUALS_JOB_DISMISS,
} from "../types";

/**
 * Auto-feed actuals cascade progress slice.
 *
 * State shape: { jobs: { [jobId]: JobState } }
 * Mirrors the global multi-job pattern so the ProgressNotificationStack
 * selector can render N concurrent cascade cards without special-casing.
 */
const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  fileName: null,
  importType: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  projects: [],
};

export const initialState = { jobs: {} };

function upsertScenario(job, index, patch) {
  if (index == null) return;
  const idx = job.projects.findIndex((p) => p.index === index);
  if (idx === -1) {
    job.projects.push({
      index,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      weight: 1,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

export default function autoFeedActualsReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case AUTO_FEED_ACTUALS_STARTED:
      return produce(state, (draft) => {
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest. This slice has
        // a bespoke shape (no ...payload), so apply each field only when present.
        const prev = draft.jobs[payload.jobId];
        const total = payload.total ?? payload.totalProjects;
        draft.jobs[payload.jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload.startedAt ?? 0,
          totalProjects: total ?? prev?.totalProjects ?? 0,
          importType: payload.importType ?? prev?.importType ?? null,
          etaSeconds: payload.etaSeconds ?? prev?.etaSeconds ?? null,
          completedCount: payload.completedCount ?? prev?.completedCount ?? 0,
          failedCount: payload.failedCount ?? prev?.failedCount ?? 0,
          jobId: payload.jobId,
        };
      });

    case AUTO_FEED_ACTUALS_PROJECT_STARTED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertScenario(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? 0,
        });
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AUTO_FEED_ACTUALS_PROJECT_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertScenario(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.COMPLETED,
          completedAt: payload.completedAt ?? 0,
          result: payload.result ?? null,
          error: null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AUTO_FEED_ACTUALS_PROJECT_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertScenario(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.FAILED,
          completedAt: payload.completedAt ?? 0,
          error: payload.error || "Unknown error",
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AUTO_FEED_ACTUALS_ALL_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case AUTO_FEED_ACTUALS_ALL_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Auto-feed cascade failed";
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case AUTO_FEED_ACTUALS_JOB_DISMISS:
      return produce(state, (draft) => {
        if (payload?.jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else if (payload?.jobId) {
          delete draft.jobs[payload.jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
